// Per-agent chip slice derived from the shared payments profile: join
// subscriptions[] + agentCredits[] on agentId (spec §5). Nanocredits ÷ 1e9.

import { useMemo } from 'react';
import { usePaymentsProfile } from './usePaymentsProfile';
import { NANOCREDITS_PER_CREDIT } from '@/src/lib/credits';

export type AgentCredits = {
  status: 'disabled' | 'loading' | 'ready' | 'unavailable';
  balance: number | null; // credits for this agent (may be fractional; floor at display)
  granted: number | null; // credits granted via subscription (may be fractional; floor at display)
  subscription:
    | { state: 'active'; tier: string | null; renewsAt: string | null }
    | { state: 'cancelled'; burnsAt: string | null }
    | null;
  card: { brand: string; last4: string } | null;
  hasRelationship: boolean;
  depleted: boolean;
};

const EMPTY = {
  balance: null, granted: null, subscription: null, card: null,
  hasRelationship: false, depleted: false,
} as const;

export function useCredits(agentId: string): AgentCredits {
  const { status, profile } = usePaymentsProfile();

  return useMemo(() => {
    if (status !== 'ready' || !profile) return { status, ...EMPTY };

    const credit = profile.agentCredits.find((c) => c.agentId === agentId) ?? null;
    const sub = profile.subscriptions.find((s) => s.agentId === agentId) ?? null;
    if (!credit && !sub) return { status, ...EMPTY };

    const balanceNc = credit?.creditsAvailableNc ?? sub?.creditsAvailableNc ?? 0;
    const subscription = sub
      ? sub.status === 'cancelled'
        ? ({ state: 'cancelled', burnsAt: sub.renewsAt } as const)
        : ({ state: 'active', tier: sub.tierId, renewsAt: sub.renewsAt } as const)
      : null;

    return {
      status,
      balance: balanceNc / NANOCREDITS_PER_CREDIT,
      granted: sub ? sub.creditsGrantedNc / NANOCREDITS_PER_CREDIT : null,
      subscription,
      card: profile.card?.brand && profile.card.last4
        ? { brand: profile.card.brand, last4: profile.card.last4 }
        : null,
      hasRelationship: true,
      depleted: balanceNc <= 0,
    };
  }, [status, profile, agentId]);
}
